/**
 * Shared building blocks for the student due-date agendas
 * (My Quizzes & Exams, My Assignments).
 *
 * Every date is read in Chicago time (the portal's business timezone),
 * and day differences are counted by calendar day, so "Tomorrow" means
 * the next calendar day in CT, not "within 24 hours".
 */
import { utcToChicago, getChicagoNow } from '../../utils/datetime/datetime-comprehensive';

export const SECTION_ORDER = ['attention', 'thisWeek', 'later', 'completed'];

export const SECTION_META = {
  attention: { label: 'Needs attention', tone: 'attention', initialLimit: Infinity },
  thisWeek: { label: 'This week', tone: 'default', initialLimit: Infinity },
  later: { label: 'Later', tone: 'default', initialLimit: 5 },
  completed: { label: 'Completed', tone: 'default', initialLimit: 5 },
};

const STRIP_DAYS = 14;
export const SOON_DAYS = 3;
export const WEEK_DAYS = 7;

/** ISO string, JS Date or Luxon DateTime → Chicago DateTime (or null). */
export const toChicago = (value) => {
  if (!value) return null;
  try {
    if (typeof value.setZone === 'function') return value.isValid ? value.setZone('America/Chicago') : null;
    const iso = value instanceof Date ? value.toISOString() : value;
    const dt = utcToChicago(iso);
    return dt && dt.isValid ? dt : null;
  } catch {
    return null; // malformed input: treat as "no date"
  }
};

export const chicagoToday = () => getChicagoNow().startOf('day');
export const chicagoNow = () => getChicagoNow();

/** Calendar-day difference in Chicago time: positive = in the future. */
export const dayDiff = (dt, today) => Math.round(dt.startOf('day').diff(today, 'days').days);

export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const fmtTime = (dt) => dt.toFormat('h:mm a');

const fmtShort = (dt) => dt.toFormat('LLL d, h:mm a');

export const tileFor = (dt) => (dt
  ? { mon: dt.toFormat('LLL').toUpperCase(), day: dt.toFormat('d'), dow: dt.toFormat('ccc').toUpperCase() }
  : { noDate: true });

/**
 * Status pill, time line and section for something that is not yet submitted.
 * Returns { section, status, time } in the shared vocabulary.
 */
export function openTiming(due, today, { blocked = false } = {}) {
  const pastDue = !!due && due < chicagoNow();
  const diff = due ? dayDiff(due, today) : null;
  if (blocked) {
    return {
      section: 'attention',
      status: { text: 'Closed', tone: 'neutral' },
      time: pastDue ? { label: 'Closed at', value: fmtTime(due) } : { label: 'Course ended', value: '' },
    };
  }
  if (pastDue) {
    const late = Math.max(1, -diff);
    return {
      section: 'attention',
      status: { text: diff === 0 ? 'Due today' : `${plural(late, 'day')} late`, tone: 'late' },
      time: { label: 'Was due', value: fmtTime(due) },
    };
  }
  if (!due) {
    // The date tile already says "No Due Date", so the status says what that means.
    return {
      section: 'later',
      status: { text: 'Anytime', tone: 'later' },
      time: { label: 'Due at', value: 'None', hideOnDesktop: true },
    };
  }
  const text = diff === 0 ? 'Due today' : diff === 1 ? 'Tomorrow' : `In ${plural(diff, 'day')}`;
  return {
    section: diff <= WEEK_DAYS ? 'thisWeek' : 'later',
    status: { text, tone: diff <= SOON_DAYS ? 'soon' : 'later' },
    time: { label: 'Due', value: fmtTime(due) },
  };
}

/** "On time" / "2 days late" for something already submitted. */
export function submittedStatus(submitted, due) {
  if (submitted && due && submitted > due) {
    const late = Math.max(1, dayDiff(submitted, due.startOf('day')));
    return { text: `${plural(late, 'day')} late`, tone: 'soon' };
  }
  return { text: 'On time', tone: 'ok' };
}

/**
 * Everything about an extension request is shown in one line under the
 * row's button (not in the title line), e.g. "Access until Oct 22, 11:59 PM".
 */
export function buildExtensionNote(ext) {
  if (!ext?.ext) return null;
  const requested = toChicago(ext.ext.requested_at);
  const reviewed = toChicago(ext.ext.reviewed_at);
  const expires = toChicago(ext.ext.access_expires_at);
  if (ext.pending) {
    return { text: requested ? `Extension requested ${requested.toFormat('LLL d')}` : 'Extension requested', tone: 'pending' };
  }
  if (ext.approvedActive) {
    return { text: expires ? `Access until ${fmtShort(expires)}` : 'Extension approved', tone: 'ok' };
  }
  if (ext.approvedExpired && ext.submissionBlocked) {
    return { text: expires ? `Extension expired ${fmtShort(expires)}` : 'Extension expired', tone: 'neutral' };
  }
  if (ext.denied && ext.submissionBlocked) {
    return { text: reviewed ? `Extension denied ${reviewed.toFormat('LLL d')}` : 'Extension denied', tone: 'late' };
  }
  return null;
}

export function extensionCta(ext) {
  return ext.canRequest
    ? { label: 'Request Extension', variant: 'attention', icon: 'extension', action: 'requestExtension' }
    : { label: 'Pending Review', variant: 'disabled', icon: 'pending', action: null };
}

/**
 * Group built rows into the four sections and build the two-week strip.
 * Rows need: section, sortSubmitted, and for the strip: stripDue (DateTime),
 * stripKind ('exam' | 'soon' | 'quiz'), title, anchor, id.
 * Completed shows the latest submission first unless a title sort is chosen.
 */
export function assembleAgenda(rows, { sortBy } = {}) {
  const today = chicagoToday();
  const grouped = { attention: [], thisWeek: [], later: [], completed: [] };
  rows.forEach((row) => grouped[row.section].push(row));
  if (!String(sortBy || '').startsWith('title')) {
    grouped.completed.sort((x, y) => y.sortSubmitted - x.sortSubmitted);
  }
  const sections = SECTION_ORDER
    .filter((key) => grouped[key].length > 0)
    .map((key) => ({ key, ...SECTION_META[key], rows: grouped[key] }));

  const days = [];
  for (let n = 0; n < STRIP_DAYS; n += 1) {
    const d = today.plus({ days: n });
    days.push({
      key: d.toISODate(),
      isToday: n === 0,
      dow: n === 0 ? 'TODAY' : d.toFormat('ccc').toUpperCase(),
      num: d.toFormat('d'),
      month: d.day === 1 || n === 0 ? d.toFormat('LLL') : '',
      items: [],
    });
  }
  const byDay = Object.fromEntries(days.map((d) => [d.key, d]));
  [...grouped.attention, ...grouped.thisWeek, ...grouped.later]
    .filter((row) => row.stripDue && row.status.tone !== 'neutral' && row.status.tone !== 'late')
    .forEach((row) => {
      const day = byDay[row.stripDue.toISODate()];
      if (day) {
        day.items.push({
          id: row.id,
          anchor: row.anchor,
          title: row.stripTitle || row.title,
          time: fmtTime(row.stripDue),
          kind: row.stripKind || (row.status.tone === 'soon' ? 'soon' : 'quiz'),
        });
      }
    });
  const stripCount = days.reduce((n, d) => n + d.items.length, 0);

  return { sections, days, stripCount };
}
